import { useEffect, useRef, useState } from 'react';
import { apiClient, ApiError } from '../lib/api';

interface AmbientAiBarProps {
  /** 'skill' = SKILL.md active. 'file' = a reference file is active. */
  scope: 'skill' | 'file';
  skillId: string;
  activeBody: string;
  activePath: string;
  onApplyToBody: (next: string) => void;
}

const QUICK_CHIPS = [
  { label: 'Tighten', prompt: 'Tighten the active body. Same meaning. Fewer words.' },
  {
    label: 'Add a gotcha',
    prompt:
      'Suggest one realistic gotcha section that this skill is missing. Return the gotcha as ready-to-paste markdown.',
  },
  {
    label: 'Rewrite description',
    prompt: 'Rewrite the skill description to be one tight sentence.',
  },
] as const;

/**
 * Bottom-of-editor ambient AI bar — Mintlify style. ⌘I focuses from
 * anywhere in the editor. Quick-chip presets on the left, prompt input
 * spanning the rest. Shares the broker.call backend with the popover
 * surfaces (description-critic / body-critic / rewrite-selection), so
 * there's one diff/accept story across the editor.
 */
export function AmbientAiBar({
  scope,
  skillId,
  activeBody,
  activePath,
  onApplyToBody,
}: AmbientAiBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  // ⌘I or Ctrl+I focuses the bar.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'i') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const runPrompt = async (userPrompt: string) => {
    if (!userPrompt.trim()) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const scopeLabel = scope === 'skill' ? 'SKILL.md (skill scope)' : `${activePath} (file scope)`;
      const full = `Scope: ${scopeLabel}\n\nCurrent body:\n\n${activeBody}\n\n---\n\nUser request:\n${userPrompt}`;
      const response = await apiClient.broker.call({
        role: 'inline-rewrite',
        prompt: full,
        skillId,
      });
      setResult(response.text);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'AI call failed');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void runPrompt(prompt);
  };

  const accept = () => {
    if (!result) return;
    onApplyToBody(result);
    setResult(null);
    setPrompt('');
  };

  const dismiss = () => {
    setResult(null);
    setError(null);
  };

  return (
    <div className="hairline-t bg-surface px-3 py-2">
      {result && (
        <div className="hairline-b mb-2 max-h-[40vh] overflow-y-auto rounded border bg-canvas p-3">
          <div className="mb-2 flex items-center justify-between text-[10px] uppercase tracking-wider text-text-subtle">
            <span>
              AI response · {scope === 'skill' ? 'skill scope' : `file scope · ${activePath}`}
            </span>
            <button
              type="button"
              onClick={dismiss}
              className="text-text-subtle hover:text-[var(--color-fg)]"
            >
              ✕
            </button>
          </div>
          <pre className="whitespace-pre-wrap font-mono text-[12px] leading-[18px]">{result}</pre>
          <div className="mt-3 flex gap-2 text-[11px]">
            <button onClick={accept} className="btn btn-primary">
              Replace body
            </button>
            <button onClick={dismiss} className="btn">
              Discard
            </button>
          </div>
        </div>
      )}
      {error && (
        <p className="mb-2 rounded border bg-danger-soft p-2 text-[11px] text-[var(--color-danger)]">
          {error}
        </p>
      )}
      <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <div className="flex shrink-0 gap-1">
          {QUICK_CHIPS.map((chip) => (
            <button
              key={chip.label}
              type="button"
              onClick={() => void runPrompt(chip.prompt)}
              disabled={busy}
              className="rounded-full border bg-surface-2 px-2.5 py-1 text-[11px] text-text-muted hover:text-[var(--color-fg)] hover:border-[var(--color-fg)] disabled:opacity-40"
            >
              {chip.label}
            </button>
          ))}
        </div>
        <input
          ref={inputRef}
          type="text"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={busy ? 'Thinking…' : `Ask AI to change ${scope === 'skill' ? 'SKILL.md' : activePath} — ⌘I to focus`}
          disabled={busy}
          className="flex-1 rounded border bg-canvas px-2 py-1 text-[12px] outline-none focus:border-[var(--color-accent)]"
        />
        <button type="submit" disabled={busy || !prompt.trim()} className="btn btn-primary">
          {busy ? '…' : '→'}
        </button>
      </form>
    </div>
  );
}
